import type { DateRange, FuelCosts, MaintenanceCosts, PageableContent, Sort, UUID } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import type { Configuration } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type { IRequest } from '../../request/Types';
import { toFuelCosts, toMaintenanceCosts, toVehicleRoute, toVehicleRoutesPage } from './APITransformer';
import type { CostsDriverIdleEngineAggregation, CostsIdleEngineEvent, CostsVehicleIdleEngineAggregation, VehicleRoute, VehicleRouteFilters } from './Types';

export class CostsRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getFuelCosts = (period: DateRange): Promise<FuelCosts> => {
    const tz = TimeZone.getEncodedTimeZone();
    const requestData: IRequest = {
      endpoint: `/costs/fuel?begin=${period.start}&end=${period.end}&tz=${tz}`,
      method: HttpMethod.GET,
    };
    return this.requester.request(requestData).then((response) => toFuelCosts(response.data));
  };

  public getMaintenanceCosts = (period: DateRange): Promise<MaintenanceCosts> => {
    const tz = TimeZone.getEncodedTimeZone();
    const requestData: IRequest = {
      endpoint: `/costs/maintenance?begin=${period.start}&end=${period.end}&tz=${tz}`,
      method: HttpMethod.GET,
    };
    return this.requester.request(requestData).then((response) => toMaintenanceCosts(response.data));
  };

  public getCostsReport = (period: DateRange): Promise<Blob> => {
    const tz = TimeZone.getEncodedTimeZone();
    const requestData: IRequest = {
      endpoint: `/costs/report?begin=${period.start}&end=${period.end}&tz=${tz}`,
      method: HttpMethod.GET,
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };

  public getVehicleRoutes = (period: DateRange, page: number, size: number, filters?: VehicleRouteFilters, sort?: Sort): Promise<PageableContent<VehicleRoute>> => {
    const tz = TimeZone.getEncodedTimeZone();
    const searchQuery = filters?.searchQuery?.trim();
    const searchQueryParam = searchQuery ? `&search_query=${encodeURIComponent(searchQuery)}` : '';
    const requestData: IRequest = {
      endpoint: `/costs/vehicle-routes?begin=${period.start}&end=${period.end}&tz=${tz}&page=${page}&size=${size}${
        filters?.wantedVehicleIds ? `&wanted_vehicle_ids=${filters.wantedVehicleIds}` : ''
      }${filters?.wantedGroups ? `&wanted_vehicle_group_ids=${filters.wantedGroups}` : ''}${sort ? `&sort_order=${sort.order}` : ''}${searchQueryParam}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => toVehicleRoutesPage(response.data));
  };

  public getVehicleRoute = (period: DateRange, vehicleId: UUID): Promise<VehicleRoute> => {
    const tz = TimeZone.getEncodedTimeZone();
    const requestData: IRequest = {
      endpoint: `/costs/vehicle-route?begin=${period.start}&end=${period.end}&tz=${tz}&vehicle_id=${vehicleId}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => toVehicleRoute(response.data[0]));
  };

  public getVehicleIdleEngineAggregations = async (period: DateRange, idleEngineThresholdInMinutes?: number): Promise<CostsVehicleIdleEngineAggregation[]> => {
    const tz = TimeZone.getEncodedTimeZone();
    const idleEngineThresholdParameter = (idleEngineThresholdInMinutes !== undefined && `&idle_engine_threshold_in_minutes=${idleEngineThresholdInMinutes}`) || '';

    const requestData: IRequest = {
      endpoint: `/costs/idle-engine/vehicle?begin=${period.start}&end=${period.end}&tz=${tz}${idleEngineThresholdParameter}`,
      method: HttpMethod.GET,
    };

    const apiData = await this.requester.request(requestData);

    return camelcaseKeys(apiData.data, { deep: true });
  };

  public getDriverIdleEngineAggregations = async (period: DateRange, idleEngineThresholdInMinutes?: number): Promise<CostsDriverIdleEngineAggregation[]> => {
    const tz = TimeZone.getEncodedTimeZone();
    const idleEngineThresholdParameter = (idleEngineThresholdInMinutes !== undefined && `&idle_engine_threshold_in_minutes=${idleEngineThresholdInMinutes}`) || '';

    const requestData: IRequest = {
      endpoint: `/costs/idle-engine/driver?begin=${period.start}&end=${period.end}&tz=${tz}${idleEngineThresholdParameter}`,
      method: HttpMethod.GET,
    };

    const apiData = await this.requester.request(requestData);

    return camelcaseKeys(apiData.data, { deep: true });
  };

  public getVehicleIdleEngineEvents = async (period: DateRange, vehicleId: UUID, idleEngineThresholdInMinutes?: number): Promise<CostsIdleEngineEvent[]> => {
    const tz = TimeZone.getEncodedTimeZone();
    const idleEngineThresholdParameter = (idleEngineThresholdInMinutes && `&idle_engine_threshold_in_minutes=${idleEngineThresholdInMinutes}`) || '';

    const requestData: IRequest = {
      endpoint: `/costs/idle-engine/vehicle/events?begin=${period.start}&end=${period.end}&tz=${tz}&vehicle_id=${vehicleId}${idleEngineThresholdParameter}`,
      method: HttpMethod.GET,
    };

    const apiData = await this.requester.request(requestData);

    return camelcaseKeys(apiData.data, { deep: true });
  };

  public getDriverIdleEngineEvents = async (period: DateRange, driverId: UUID, idleEngineThresholdInMinutes?: number): Promise<CostsIdleEngineEvent[]> => {
    const tz = TimeZone.getEncodedTimeZone();
    const idleEngineThresholdParameter = (idleEngineThresholdInMinutes && `&idle_engine_threshold_in_minutes=${idleEngineThresholdInMinutes}`) || '';

    const requestData: IRequest = {
      endpoint: `/costs/idle-engine/driver/events?begin=${period.start}&end=${period.end}&tz=${tz}&driver_id=${driverId}${idleEngineThresholdParameter}`,
      method: HttpMethod.GET,
    };

    const apiData = await this.requester.request(requestData);

    return camelcaseKeys(apiData.data, { deep: true });
  };
}

export default CostsRequester;
